import type { UIMessage } from 'ai';

import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import { CONTENT_REPORT_LIMITS } from '@/types/content-report';
import type { ContentReportResult, PendingReportContext } from '@/types/content-report';
import type { HomeSummary } from '@/types/home-summary';
import type { AISuggestion } from '@/types/ai-suggestions';

/**
 * ContentReportManager — owns both shaping AI artefacts into a reportable
 * context and submitting reports to the server (which forwards them to the
 * operator inbox).
 *
 * Two responsibilities, two entry points:
 *   - `buildSummaryContext()` runs at the source surface (it has the artefact)
 *     to produce the snapshot stashed for the report screen.
 *   - `submit()` runs on the report screen with the stashed context + reason.
 */
export class ContentReportManager {
  /**
   * Shape a home summary into a reportable context (snapshot + metadata).
   */
  buildSummaryContext(summary: HomeSummary): PendingReportContext {
    return {
      contentType: 'home-summary',
      contentId: summary.id,
      variant: summary.variant,
      generatedAt: summary.generatedAt,
      snapshot: this.buildSummarySnapshot(summary),
    };
  }

  /**
   * Shape an AI chat message into a reportable context (its text content).
   */
  buildChatContext(message: UIMessage): PendingReportContext {
    const text = message.parts
      .filter((part): part is { type: 'text'; text: string } => part.type === 'text')
      .map((part) => part.text)
      .join('\n')
      .trim();

    return {
      contentType: 'chat',
      contentId: message.id,
      variant: null,
      generatedAt: null,
      snapshot: (text || '(no text content)').slice(0, CONTENT_REPORT_LIMITS.SNAPSHOT_MAX_CHARS),
    };
  }

  /**
   * Shape the currently-shown AI suggestions into a single reportable context.
   */
  buildSuggestionsContext(suggestions: AISuggestion[]): PendingReportContext {
    const snapshot = suggestions
      .map((suggestion) => {
        const lines = [`• ${suggestion.title}`, suggestion.description];
        for (const action of suggestion.actions ?? []) {
          lines.push(`  - ${action}`);
        }
        return lines.join('\n');
      })
      .join('\n\n')
      .slice(0, CONTENT_REPORT_LIMITS.SNAPSHOT_MAX_CHARS);

    return {
      contentType: 'ai-suggestion',
      contentId: suggestions[0]?.id ?? 'ai-suggestions',
      variant: null,
      generatedAt: null,
      snapshot: snapshot || '(no suggestions)',
    };
  }

  /**
   * Submit a report for a previously-built context, with an optional reason.
   */
  async submit(context: PendingReportContext, reason?: string): Promise<ContentReportResult> {
    return this.post({
      contentType: context.contentType,
      contentId: context.contentId,
      variant: context.variant,
      generatedAt: context.generatedAt,
      contentSnapshot: context.snapshot,
      reason: reason?.trim() || null,
    });
  }

  /**
   * Submit a context-free report (raised from Settings). A reason is required
   * since there is no attached content to describe the issue.
   */
  async submitGeneric(reason?: string): Promise<ContentReportResult> {
    return this.post({
      contentType: 'general',
      contentId: null,
      variant: null,
      generatedAt: null,
      contentSnapshot: null,
      reason: reason?.trim() || null,
    });
  }

  private async post(payload: Record<string, unknown>): Promise<ContentReportResult> {
    let response: Response;
    try {
      response = await authenticatedFetch.fetch('/api/report-ai-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Network error' };
    }

    if (!response.ok) {
      let message = 'Failed to submit report';
      try {
        const body = (await response.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch {
        // tolerate missing/non-JSON body
      }
      return { success: false, error: message };
    }

    return { success: true };
  }

  private buildSummarySnapshot(summary: HomeSummary): string {
    const lines: string[] = [summary.headline];
    if (summary.summaryText) {
      lines.push(summary.summaryText);
    }
    for (const section of summary.sections) {
      if (section.title) lines.push(`# ${section.title}`);
      if (section.body) lines.push(section.body);
      for (const bullet of section.bullets ?? []) {
        lines.push(`- ${bullet.text}`);
      }
    }
    return lines.join('\n').slice(0, CONTENT_REPORT_LIMITS.SNAPSHOT_MAX_CHARS);
  }
}

export const contentReportManager = new ContentReportManager();
